/**
 * SplashGrid - Letter-Based Logo Tiling Component
 *
 * Creates a grid where individual B, O, X letters are placed in cells
 * following an 8×6 pattern that repeats across the screen.
 * Letters are revealed on hover with a radial gradient effect.
 */

// Pattern tile dimensions (8 columns × 6 rows)
const TILE_COLS = 8;
const TILE_ROWS = 6;

// Letter pattern: 8 columns × 6 rows
// null = empty cell, 'B'/'O'/'X' = that letter
const LOGO_PATTERN = [
  ['O', 'X', null, 'X', 'O', 'X', null, 'X'],  // Row 0
  ['B', null, 'B', 'O', 'B', null, 'B', 'O'],  // Row 1
  ['B', 'O', 'O', 'X', 'O', 'X', 'B', 'O'],    // Row 2
  [null, 'X', 'B', null, 'B', 'O', null, 'X'], // Row 3
  ['B', null, 'O', 'X', 'O', 'X', 'B', null],  // Row 4
  ['O', 'X', 'B', null, 'B', 'O', 'O', 'X']    // Row 5
];

// Content layout configuration (cell coordinates, local to group)
const CONTENT_LAYOUT = {
  // Group bounding box
  groupWidth: 13,  // cells
  groupHeight: 6,  // cells

  // Element positions (local to group)
  logo: { col: 0, row: 0, width: 2, height: 4 },
  tagline: { col: 3, row: 3, width: 7, height: 1 },
  formRow: { col: 0, row: 5, width: 13, height: 1 }
};

// Mobile layout configuration (< 760px viewport)
const CONTENT_LAYOUT_MOBILE = {
  // Group bounding box - narrower for mobile (viewport_cols - 2 for left/right gaps)
  groupWidth: 5,
  groupHeight: 9,

  // All elements start at col 0 within group (group is offset by 1 cell from edges)
  logo: { col: 0, row: 0, width: 2, height: 4 },
  tagline: { col: 0, row: 5, width: 5, height: 1 },  // 1 cell below logo, left-aligned
  formRow: { col: 0, row: 7, width: 5, height: 2 }   // below tagline
};

class SplashGrid extends HTMLElement {
  constructor() {
    super();

    // Configuration from data attributes
    this.cellSize = parseInt(this.dataset.cellSize) || 68;
    this.fadeDuration = parseInt(this.dataset.fadeDuration) || 400;
    this.revealRadius = parseInt(this.dataset.revealRadius) || 200;
    this.baseOpacity = parseFloat(this.dataset.baseOpacity) || 0;

    // State
    this.cells = [];
    this.mouseX = -1000;
    this.mouseY = -1000;
    this.isAnimating = false;
    this.letterImages = {}; // { B: Image, O: Image, X: Image }
    this.lettersLoaded = false;

    // Performance
    this.lastFrameTime = 0;
    this.targetFPS = 60;
    this.frameInterval = 1000 / this.targetFPS;

    // Bound methods for event listeners
    this.handleMouseMove = this.throttle(this.onMouseMove.bind(this), 16);
    this.handleTouchMove = this.throttle(this.onTouchMove.bind(this), 16);
    this.handleMouseLeave = this.onMouseLeave.bind(this);
    this.handleResize = this.debounce(this.onResize.bind(this), 150);
  }

  connectedCallback() {
    this.canvas = this.querySelector('.splash-page__canvas');
    if (!this.canvas) return;

    this.ctx = this.canvas.getContext('2d');

    // Load letter images
    this.loadLetters().then(() => {
      this.init();
      this.bindEvents();
      this.startAnimation();
    });
  }

  disconnectedCallback() {
    this.stopAnimation();
    this.unbindEvents();
  }

  /**
   * Load individual letter SVGs as images for canvas rendering
   */
  async loadLetters() {
    const letters = ['B', 'O', 'X'];
    const urls = {
      B: this.dataset.letterBUrl,
      O: this.dataset.letterOUrl,
      X: this.dataset.letterXUrl
    };

    const loadPromises = letters.map(letter => {
      return new Promise((resolve) => {
        const url = urls[letter];
        if (!url) {
          console.warn(`SplashGrid: No URL for letter ${letter}`);
          resolve();
          return;
        }

        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          this.letterImages[letter] = img;
          resolve();
        };
        img.onerror = () => {
          console.warn(`SplashGrid: Failed to load letter ${letter}`);
          resolve();
        };
        img.src = url;
      });
    });

    await Promise.all(loadPromises);
    this.lettersLoaded = Object.keys(this.letterImages).length > 0;
  }

  /**
   * Initialize canvas and cell grid
   */
  init() {
    this.setCanvasSize();
    this.createCells();
    this.positionContentElements();
    this.exclusionZones = this.getExclusionZones();
  }

  /**
   * Get exclusion zones from cell coordinates (guaranteed grid-aligned)
   */
  getExclusionZones() {
    const offset = this.calculateGridOffset();
    const zones = [];

    // Use the active layout (set by calculateGridOffset)
    const layout = this.activeLayout || CONTENT_LAYOUT;

    const elements = [
      layout.logo,
      layout.tagline,
      layout.formRow,
      this.footerLayout
    ].filter(Boolean);

    for (const elementLayout of elements) {
      // Footer uses absolute positioning, others use offset
      const colStart = elementLayout.isAbsolute ? elementLayout.col : offset.col + elementLayout.col;
      const rowStart = elementLayout.isAbsolute ? elementLayout.row : offset.row + elementLayout.row;

      zones.push({
        left: colStart * this.cellSize,
        top: rowStart * this.cellSize,
        right: (colStart + elementLayout.width) * this.cellSize,
        bottom: (rowStart + elementLayout.height) * this.cellSize
      });
    }

    return zones;
  }

  /**
   * Calculate the grid cell offset to center the content group
   * Returns { col, row } offset to add to local element positions
   */
  calculateGridOffset() {
    const viewportCols = Math.floor(this.width / this.cellSize);
    const viewportRows = Math.floor(this.height / this.cellSize);

    // Use mobile layout for narrow viewports
    const layout = this.width < 760 ? CONTENT_LAYOUT_MOBILE : CONTENT_LAYOUT;
    this.activeLayout = layout;

    // For mobile: position 1 cell from left edge, shifted up to accommodate taller footer
    if (this.width < 760) {
      const offsetRow = Math.floor((viewportRows - layout.groupHeight) / 2) - 2; // Shift up by 2 cells
      return {
        col: 1, // 1 cell gap from left
        row: Math.max(1, offsetRow) // ensure at least 1 cell from top
      };
    }

    // Desktop: center the content group
    const offsetCol = Math.floor((viewportCols - layout.groupWidth) / 2);
    const offsetRow = Math.floor((viewportRows - layout.groupHeight) / 2);

    return {
      col: Math.max(0, offsetCol),
      row: Math.max(0, offsetRow)
    };
  }

  /**
   * Position content elements using CSS Grid based on cell coordinates
   */
  positionContentElements() {
    const splash = this.closest('.splash-page');
    if (!splash) return;

    const offset = this.calculateGridOffset();
    const contentEl = splash.querySelector('.splash-page__content');

    if (!contentEl) return;

    // Set grid template based on viewport size
    const cols = Math.ceil(this.width / this.cellSize);
    const rows = Math.ceil(this.height / this.cellSize);

    contentEl.style.gridTemplateColumns = `repeat(${cols}, ${this.cellSize}px)`;
    contentEl.style.gridTemplateRows = `repeat(${rows}, ${this.cellSize}px)`;

    // Use the active layout (set by calculateGridOffset)
    const layout = this.activeLayout || CONTENT_LAYOUT;

    // Position each content element
    const elements = {
      '.splash-page__logo': layout.logo,
      '.splash-page__tagline': layout.tagline,
      '.splash-page__form-row': layout.formRow
    };

    for (const [selector, elementLayout] of Object.entries(elements)) {
      const el = splash.querySelector(selector);
      if (!el) continue;

      const colStart = offset.col + elementLayout.col + 1; // CSS Grid is 1-indexed
      const rowStart = offset.row + elementLayout.row + 1;

      el.style.gridColumn = `${colStart} / span ${elementLayout.width}`;
      el.style.gridRow = `${rowStart} / span ${elementLayout.height}`;
    }

    // Position footer at bottom of viewport
    const footer = splash.querySelector('.splash-page__footer');
    if (footer) {
      const isMobile = this.width < 760;
      const footerHeight = isMobile ? 3 : 1; // 3 cells tall on mobile, 1 on desktop
      let footerRow = rows - 1 - footerHeight + 1; // Position so bottom edge is 1 cell from viewport bottom

      // Ensure footer doesn't overlap with content (at least 1 cell gap)
      const contentEndRow = offset.row + layout.groupHeight;
      const minFooterRow = contentEndRow + 2; // At least 1 cell gap after content
      if (footerRow < minFooterRow) {
        footerRow = minFooterRow;
      }

      const footerCol = isMobile ? 1 : 0; // Start at col 1 on mobile to align with content
      const footerWidth = isMobile ? cols - 2 : cols; // Adjust width on mobile (1 cell gap on each side)
      this.footerLayout = { col: footerCol, row: footerRow - 1, width: footerWidth, height: footerHeight, isAbsolute: true };
      footer.style.gridColumn = `${footerCol + 1} / span ${footerWidth}`;
      footer.style.gridRow = `${footerRow} / span ${footerHeight}`;
    }

    // Show content now that it's positioned
    contentEl.classList.add('is-ready');
  }

  setCanvasSize() {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.getBoundingClientRect();

    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    this.canvas.style.width = `${rect.width}px`;
    this.canvas.style.height = `${rect.height}px`;

    this.ctx.scale(dpr, dpr);

    this.width = rect.width;
    this.height = rect.height;

    // Calculate grid dimensions
    this.cols = Math.ceil(this.width / this.cellSize) + 1;
    this.rows = Math.ceil(this.height / this.cellSize) + 1;
  }

  /**
   * Create the cell grid with tile-based pattern
   * Each cell knows which letter (if any) to display based on its position
   * within the repeating 8×6 tile unit
   */
  createCells() {
    this.cells = [];

    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        // Which tile unit does this cell belong to?
        const tileCol = Math.floor(col / TILE_COLS);
        const tileRow = Math.floor(row / TILE_ROWS);

        // Get tile rotation (seeded for consistency)
        const tileSeed = tileRow * 1000 + tileCol;
        const rotation = this.getTileRotation(tileSeed);

        // Position within the 8×6 tile (0-7 for col, 0-5 for row)
        const localCol = col % TILE_COLS;
        const localRow = row % TILE_ROWS;

        // Get the letter at this position (may be null for empty cells)
        const letter = LOGO_PATTERN[localRow]?.[localCol] || null;

        this.cells.push({
          row,
          col,
          letter,
          rotation,
          opacity: this.baseOpacity,
          targetOpacity: this.baseOpacity
        });
      }
    }
  }

  /**
   * Get rotation for a tile based on seed (deterministic)
   */
  getTileRotation(seed) {
    const rotations = [0, 90, 180, 270];
    // Use a simple seeded random based on sin
    const rand = Math.abs(Math.sin(seed * 12.9898) * 43758.5453) % 1;
    return rotations[Math.floor(rand * 4)];
  }

  bindEvents() {
    // Enable pointer events on parent for mouse tracking
    const splash = this.closest('.splash-page');
    if (splash) {
      splash.addEventListener('mousemove', this.handleMouseMove);
      splash.addEventListener('mouseleave', this.handleMouseLeave);
      splash.addEventListener('touchmove', this.handleTouchMove, { passive: true });
      splash.addEventListener('touchstart', this.handleTouchMove, { passive: true });
    }

    window.addEventListener('resize', this.handleResize);
  }

  unbindEvents() {
    const splash = this.closest('.splash-page');
    if (splash) {
      splash.removeEventListener('mousemove', this.handleMouseMove);
      splash.removeEventListener('mouseleave', this.handleMouseLeave);
      splash.removeEventListener('touchmove', this.handleTouchMove);
      splash.removeEventListener('touchstart', this.handleTouchMove);
    }

    window.removeEventListener('resize', this.handleResize);
  }

  /**
   * Mouse move handler - update cursor position
   */
  onMouseMove(event) {
    const rect = this.getBoundingClientRect();
    this.mouseX = event.clientX - rect.left;
    this.mouseY = event.clientY - rect.top;
    this.updateCellTargets();
  }

  /**
   * Mouse leave handler - reset cursor position
   */
  onMouseLeave() {
    this.mouseX = -1000;
    this.mouseY = -1000;
    // Set all cells to fade back to base
    for (const cell of this.cells) {
      cell.targetOpacity = this.baseOpacity;
    }
  }

  /**
   * Touch move handler - same behavior as mouse
   */
  onTouchMove(event) {
    if (event.touches.length > 0) {
      const touch = event.touches[0];
      const rect = this.getBoundingClientRect();
      this.mouseX = touch.clientX - rect.left;
      this.mouseY = touch.clientY - rect.top;
      this.updateCellTargets();
    }
  }

  /**
   * Window resize handler
   */
  onResize() {
    this.init();
  }

  /**
   * Update target opacity for all cells based on cursor position
   */
  updateCellTargets() {
    const maxOpacity = 0.7;
    const neighborBaseOpacity = 0.02;
    const neighborMaxOpacity = 0.3;

    // Determine which cell the cursor is over
    const cursorCol = Math.floor(this.mouseX / this.cellSize);
    const cursorRow = Math.floor(this.mouseY / this.cellSize);

    // Calculate mouse position within the center cell (0-1)
    const cellLocalX = (this.mouseX % this.cellSize) / this.cellSize;
    const cellLocalY = (this.mouseY % this.cellSize) / this.cellSize;

    // Calculate proximity to each edge (0 = center, 1 = at edge)
    const proximityLeft = 1 - cellLocalX;   // High when mouse is left
    const proximityRight = cellLocalX;       // High when mouse is right
    const proximityTop = 1 - cellLocalY;     // High when mouse is top
    const proximityBottom = cellLocalY;      // High when mouse is bottom

    // Apply threshold: only start lighting up when past center (> 0.5)
    const threshold = 0.5;
    const calcEdgeOpacity = (proximity) => {
      if (proximity <= threshold) return 0;
      // Map 0.5-1.0 to 0.1-0.3
      const t = (proximity - threshold) / (1 - threshold);
      return neighborBaseOpacity + t * (neighborMaxOpacity - neighborBaseOpacity);
    };

    for (const cell of this.cells) {
      const colDiff = cell.col - cursorCol;
      const rowDiff = cell.row - cursorRow;

      if (colDiff === 0 && rowDiff === 0) {
        // Center cell - full brightness
        cell.targetOpacity = maxOpacity;
      } else if (Math.abs(colDiff) <= 1 && Math.abs(rowDiff) <= 1) {
        // Neighboring cell (within 3x3 grid)
        if (colDiff === 0 && rowDiff === -1) {
          // Top neighbor
          cell.targetOpacity = calcEdgeOpacity(proximityTop);
        } else if (colDiff === 0 && rowDiff === 1) {
          // Bottom neighbor
          cell.targetOpacity = calcEdgeOpacity(proximityBottom);
        } else if (colDiff === -1 && rowDiff === 0) {
          // Left neighbor
          cell.targetOpacity = calcEdgeOpacity(proximityLeft);
        } else if (colDiff === 1 && rowDiff === 0) {
          // Right neighbor
          cell.targetOpacity = calcEdgeOpacity(proximityRight);
        } else if (colDiff === -1 && rowDiff === -1) {
          // Top-left diagonal - use min of both proximities
          cell.targetOpacity = calcEdgeOpacity(Math.min(proximityTop, proximityLeft));
        } else if (colDiff === 1 && rowDiff === -1) {
          // Top-right diagonal
          cell.targetOpacity = calcEdgeOpacity(Math.min(proximityTop, proximityRight));
        } else if (colDiff === -1 && rowDiff === 1) {
          // Bottom-left diagonal
          cell.targetOpacity = calcEdgeOpacity(Math.min(proximityBottom, proximityLeft));
        } else if (colDiff === 1 && rowDiff === 1) {
          // Bottom-right diagonal
          cell.targetOpacity = calcEdgeOpacity(Math.min(proximityBottom, proximityRight));
        }
      } else {
        // Outside 3x3 - fade to base
        cell.targetOpacity = this.baseOpacity;
      }
    }
  }

  /**
   * Animation loop
   */
  startAnimation() {
    if (this.isAnimating) return;
    this.isAnimating = true;
    this.animate();
  }

  stopAnimation() {
    this.isAnimating = false;
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
  }

  animate(currentTime = 0) {
    if (!this.isAnimating) return;

    this.animationFrame = requestAnimationFrame(this.animate.bind(this));

    // Frame rate limiting
    const elapsed = currentTime - this.lastFrameTime;
    if (elapsed < this.frameInterval) return;
    this.lastFrameTime = currentTime - (elapsed % this.frameInterval);

    this.update();
    this.draw();
  }

  /**
   * Update cell opacities with smooth interpolation
   * Uses ease-out easing: faster when far from target, slower as we approach
   */
  update() {
    const fadeInFactor = 0.18;   // Faster fade-in (was 0.12)
    const fadeOutFactor = 0.10;  // Faster fade-out (was 0.06)

    for (const cell of this.cells) {
      const diff = cell.targetOpacity - cell.opacity;
      if (Math.abs(diff) > 0.001) {
        const baseFactor = diff > 0 ? fadeInFactor : fadeOutFactor;
        // Ease-out: faster when far from target, slower as we approach
        const distance = Math.abs(diff);
        const easedFactor = baseFactor * (0.5 + distance * 0.5);
        cell.opacity += diff * easedFactor;
      } else {
        cell.opacity = cell.targetOpacity;
      }
    }
  }

  /**
   * Draw grid lines, skipping segments inside exclusion zones
   */
  drawGrid() {
    const zones = this.exclusionZones || [];

    this.ctx.strokeStyle = '#2d2d2d';
    this.ctx.lineWidth = 1;

    // Draw vertical lines (skip segments inside zones)
    for (let x = 0; x <= this.width; x += this.cellSize) {
      const segments = this.getLineSegments(x, 0, x, this.height, zones, 'vertical');
      for (const seg of segments) {
        this.ctx.beginPath();
        this.ctx.moveTo(seg.x1, seg.y1);
        this.ctx.lineTo(seg.x2, seg.y2);
        this.ctx.stroke();
      }
    }

    // Draw horizontal lines (skip segments inside zones)
    for (let y = 0; y <= this.height; y += this.cellSize) {
      const segments = this.getLineSegments(0, y, this.width, y, zones, 'horizontal');
      for (const seg of segments) {
        this.ctx.beginPath();
        this.ctx.moveTo(seg.x1, seg.y1);
        this.ctx.lineTo(seg.x2, seg.y2);
        this.ctx.stroke();
      }
    }

    // Draw perimeter outline around each cutout
    for (const zone of zones) {
      this.ctx.beginPath();
      this.ctx.moveTo(zone.left, zone.top);
      this.ctx.lineTo(zone.right, zone.top);
      this.ctx.stroke();

      this.ctx.beginPath();
      this.ctx.moveTo(zone.left, zone.bottom);
      this.ctx.lineTo(zone.right, zone.bottom);
      this.ctx.stroke();

      this.ctx.beginPath();
      this.ctx.moveTo(zone.left, zone.top);
      this.ctx.lineTo(zone.left, zone.bottom);
      this.ctx.stroke();

      this.ctx.beginPath();
      this.ctx.moveTo(zone.right, zone.top);
      this.ctx.lineTo(zone.right, zone.bottom);
      this.ctx.stroke();
    }
  }

  /**
   * Split a line into segments that don't intersect exclusion zones
   */
  getLineSegments(x1, y1, x2, y2, zones, orientation) {
    const cuts = [];

    for (const zone of zones) {
      if (orientation === 'vertical') {
        if (x1 >= zone.left && x1 <= zone.right) {
          cuts.push({ pos: zone.top, type: 'enter' });
          cuts.push({ pos: zone.bottom, type: 'exit' });
        }
      } else {
        if (y1 >= zone.top && y1 <= zone.bottom) {
          cuts.push({ pos: zone.left, type: 'enter' });
          cuts.push({ pos: zone.right, type: 'exit' });
        }
      }
    }

    if (cuts.length === 0) {
      return [{ x1, y1, x2, y2 }];
    }

    cuts.sort((a, b) => a.pos - b.pos);

    const segments = [];
    const start = orientation === 'vertical' ? y1 : x1;
    const end = orientation === 'vertical' ? y2 : x2;

    let current = start;
    let inside = 0;

    for (const cut of cuts) {
      if (cut.type === 'enter') {
        if (inside === 0 && cut.pos > current) {
          if (orientation === 'vertical') {
            segments.push({ x1, y1: current, x2, y2: cut.pos });
          } else {
            segments.push({ x1: current, y1, x2: cut.pos, y2 });
          }
        }
        inside++;
      } else {
        inside--;
        if (inside === 0) {
          current = cut.pos;
        }
      }
    }

    if (inside === 0 && current < end) {
      if (orientation === 'vertical') {
        segments.push({ x1, y1: current, x2, y2: end });
      } else {
        segments.push({ x1: current, y1, x2: end, y2 });
      }
    }

    return segments;
  }

  /**
   * Draw all cells to canvas
   * Each cell draws its letter (if any) centered in the cell
   */
  draw() {
    // Clear canvas
    this.ctx.clearRect(0, 0, this.width, this.height);

    // Draw grid lines first (behind letters)
    this.drawGrid();

    if (!this.lettersLoaded) return;

    for (const cell of this.cells) {
      // Skip nearly invisible cells or cells with no letter
      if (cell.opacity <= 0.01 || !cell.letter) continue;

      const img = this.letterImages[cell.letter];
      if (!img) continue;

      // Center of the cell
      const centerX = (cell.col + 0.5) * this.cellSize;
      const centerY = (cell.row + 0.5) * this.cellSize;

      // Draw letter at 85% of cell size, centered and rotated
      const size = this.cellSize * 0.85;

      this.ctx.save();
      this.ctx.globalAlpha = cell.opacity;
      this.ctx.translate(centerX, centerY);
      this.ctx.rotate(cell.rotation * Math.PI / 180);
      this.ctx.drawImage(img, -size / 2, -size / 2, size, size);
      this.ctx.restore();
    }
  }

  /**
   * Utility: Throttle function calls
   */
  throttle(fn, wait) {
    let lastTime = 0;
    return (...args) => {
      const now = Date.now();
      if (now - lastTime >= wait) {
        lastTime = now;
        fn.apply(this, args);
      }
    };
  }

  /**
   * Utility: Debounce function calls
   */
  debounce(fn, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

// Register the custom element
if (!customElements.get('splash-grid')) {
  customElements.define('splash-grid', SplashGrid);
}
